import type { ArraySchema, BaseIssue, BaseSchema } from 'valibot'
import * as v from 'valibot'

// generic successful response schema
export const GenericSuccessResponseSchema = v.object({
  status: v.literal('OK'),
  payload: v.nonNullish(v.unknown()),
})

// generic errornous response schema
export const GenericErrorResponseSchema = v.object({
  status: v.literal('ERROR'),
  errorCode: v.pipe(v.union([v.string(), v.number()]), v.transform(String)),
  message: v.nullish(v.string()),
})

// generic response schema
export const GenericResponseSchema = v.variant('status', [
  GenericSuccessResponseSchema,
  GenericErrorResponseSchema,
])

// generic page info schema (page without content)
export const GenericPageInfoSchema = v.object({
  content: v.array(v.unknown()),
  first: v.boolean(),
  last: v.boolean(),
  number: v.number(),
  size: v.number(),
  totalElements: v.number(),
  totalPages: v.number(),
})

// generic slice info schema (slice without content)
export const GenericSliceInfoSchema = v.object({
  content: v.array(v.unknown()),
  first: v.boolean(),
  last: v.boolean(),
  number: v.number(),
  size: v.number(),
})

// datetime schema, converts ISO string to Date
export const DateTimeSchema = v.pipe(
  v.string(),
  v.isoTimestamp(),
  v.transform((t) => new Date(t))
)

/**
 * Creates a successful response schema for a given payload schema
 */
export function response<
  const TPayload extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(payload: TPayload) {
  return v.object({
    status: v.literal('OK'),
    payload,
  })
}

/**
 * Creates a schema for a page of items, in v3 WBS API
 */
export function page<
  const TContent extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(content: TContent) {
  const invalid = Symbol()
  return v.pipe(
    v.object({
      ...GenericPageInfoSchema.entries,
      content: v.array(
        v.fallback(content, (data) => {
          const err = data?.issues ? v.summarize(data?.issues) : 'unknown error'
          console.warn('Value', data?.value, 'omitted:', err)
          return invalid // for invalid array elements return special `invalid` value
        })
      ),
    }),
    v.transform((result) => ({
      ...result,
      content: result.content.filter((c) => c !== invalid), // filter out `invalid` values
    }))
  )
}

/**
 * Creates a schema for a slice of items, in v3 WBS API
 * Technically, a slice is the same as a page, but without the `totalElements` and `totalPages` fields
 */
export function slice<
  const TContent extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(content: TContent) {
  return v.object({
    ...GenericSliceInfoSchema.entries,
    content: safe_array(content, (value, error) => {
      console.warn('Value', value, 'omitted:', error)
    }),
  })
}

/**
 * Schema for an array that skips invalid items, with optional error callback
 */
export function safe_array<
  const TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(schema: TSchema, error?: (invalidValue: unknown, data: any) => void) {
  const invalid = Symbol()
  return v.pipe(
    v.array(
      v.fallback(schema, (data) => {
        error?.(data?.value, data)
        return invalid // for invalid array elements return special `invalid` value
      })
    ),
    v.transform((result) => result.filter((c) => c !== invalid)) // filter out `invalid` values
  )
}

/**
 * Schema for an array that must not be empty, with optional default value if empty
 */
export function not_empty_array<
  const TItem extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
  const TArraySchema extends ArraySchema<TItem, undefined>,
>(array: TArraySchema, def: any) {
  return v.pipe(
    array,
    v.transform((modules) => (modules.length > 0 ? modules : def))
  )
}
